import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button, Input, Card, Logo } from '../components/common';
import { findUsername, requestPasswordReset } from '../services/api/auth.api';
import { useMutation } from '@tanstack/react-query';
import { Mail, Lock, User, AlertCircle } from 'lucide-react';

type TabType = 'username' | 'password';

// 이메일 유효성 검사 정규식
const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
// 생년월일 형식 검사 (YYYY-MM-DD) - 향후 생년월일 검증에 사용
const birthdateRegex = /^\d{4}-\d{2}-\d{2}$/;
void birthdateRegex;

export default function FindAccount() {
  const [activeTab, setActiveTab] = useState<TabType>('username');
  const [email, setEmail] = useState('');
  const [username, setUsername] = useState('');
  const [birthYear, setBirthYear] = useState('');
  const [birthMonth, setBirthMonth] = useState('');
  const [birthDay, setBirthDay] = useState('');
  const [emailError, setEmailError] = useState('');
  const [usernameError, setUsernameError] = useState('');
  const [birthdateError, setBirthdateError] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);

  const findUsernameMutation = useMutation({
    mutationFn: (data: { username: string; birthdate: string }) => findUsername(data),
    onSuccess: () => {
      setIsSubmitted(true);
      setUsernameError('');
      setBirthdateError('');
    },
    onError: (error: any) => {
      const errorCode = error?.response?.data?.error?.code;
      const errorMessage = error?.response?.data?.error?.message || '아이디 찾기에 실패했습니다.';
      
      // SMTP 설정 오류인 경우
      if (errorCode === 'EMAIL_SERVICE_ERROR') {
        setUsernameError('이메일 발송 서비스에 문제가 있습니다. 관리자에게 문의해주세요.');
      } else if (errorMessage.includes('사용자명')) {
        setUsernameError(errorMessage);
      } else if (errorMessage.includes('생년월일')) {
        setBirthdateError(errorMessage);
      } else {
        setUsernameError(errorMessage);
      }
      setIsSubmitted(false);
    },
  });

  const requestPasswordResetMutation = useMutation({
    mutationFn: (email: string) => requestPasswordReset(email),
    onSuccess: () => {
      setIsSubmitted(true);
      setEmailError('');
    },
    onError: (error: any) => {
      const errorCode = error?.response?.data?.error?.code;
      const errorMessage = error?.response?.data?.error?.message || '비밀번호 재설정 요청에 실패했습니다. 이메일을 확인해주세요.';
      
      // SMTP 설정 오류인 경우
      if (errorCode === 'EMAIL_SERVICE_ERROR') {
        setEmailError('이메일 발송 서비스에 문제가 있습니다. 관리자에게 문의해주세요.');
      } else {
        setEmailError(errorMessage);
      }
      setIsSubmitted(false);
    },
  });

  const validateEmail = (emailValue: string): boolean => {
    if (!emailValue.trim()) {
      setEmailError('이메일을 입력해주세요.');
      return false;
    }
    if (!emailRegex.test(emailValue)) {
      setEmailError('유효한 이메일 주소를 입력해주세요.');
      return false;
    }
    setEmailError('');
    return true;
  };

  const validateUsername = (usernameValue: string): boolean => {
    if (!usernameValue.trim()) {
      setUsernameError('사용자명을 입력해주세요.');
      return false;
    }
    if (usernameValue.trim().length < 2) {
      setUsernameError('사용자명은 최소 2자 이상이어야 합니다.');
      return false;
    }
    setUsernameError('');
    return true;
  };

  const validateBirthdate = (): boolean => {
    if (!birthYear || !birthMonth || !birthDay) {
      setBirthdateError('생년월일을 모두 입력해주세요.');
      return false;
    }

    const year = parseInt(birthYear);
    const month = parseInt(birthMonth);
    const day = parseInt(birthDay);

    if (year < 1900 || year > new Date().getFullYear()) {
      setBirthdateError('유효한 연도를 입력해주세요.');
      return false;
    }

    if (month < 1 || month > 12) {
      setBirthdateError('유효한 월을 입력해주세요.');
      return false;
    }

    if (day < 1 || day > 31) {
      setBirthdateError('유효한 일을 입력해주세요.');
      return false;
    }

    // 날짜 유효성 검사
    const date = new Date(year, month - 1, day);
    if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
      setBirthdateError('유효한 날짜를 입력해주세요.');
      return false;
    }

    setBirthdateError('');
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (activeTab === 'username') {
      // 아이디 찾기
      if (!validateUsername(username) || !validateBirthdate()) {
        return;
      }

      setIsSubmitted(false);
      setUsernameError('');
      setBirthdateError('');

      const birthdate = `${birthYear}-${birthMonth.padStart(2, '0')}-${birthDay.padStart(2, '0')}`;
      findUsernameMutation.mutate({ username, birthdate });
    } else {
      // 비밀번호 찾기
      if (!validateEmail(email)) {
        return;
      }

      setIsSubmitted(false);
      setEmailError('');

      requestPasswordResetMutation.mutate(email);
    }
  };

  const handleTabChange = (tab: TabType) => {
    setActiveTab(tab);
    setEmail('');
    setUsername('');
    setBirthYear('');
    setBirthMonth('');
    setBirthDay('');
    setEmailError('');
    setUsernameError('');
    setBirthdateError('');
    setIsSubmitted(false);
    findUsernameMutation.reset();
    requestPasswordResetMutation.reset();
  };

  const handleReset = () => {
    setEmail('');
    setUsername('');
    setBirthYear('');
    setBirthMonth('');
    setBirthDay('');
    setEmailError('');
    setUsernameError('');
    setBirthdateError('');
    setIsSubmitted(false);
    findUsernameMutation.reset();
    requestPasswordResetMutation.reset();
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setEmail(value);
    if (emailError && value.trim()) {
      setEmailError('');
    }
  };

  const handleUsernameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setUsername(value);
    if (usernameError && value.trim()) {
      setUsernameError('');
    }
  };

  const isLoading =
    findUsernameMutation.isPending || requestPasswordResetMutation.isPending;
  const apiError =
    findUsernameMutation.error || requestPasswordResetMutation.error;
  const hasError =
    !!emailError ||
    !!usernameError ||
    !!birthdateError ||
    !!apiError;

  return (
    <div className="hell-scope relative min-h-screen flex items-center justify-center bg-[var(--bg)] p-4">
      {/* 배경 장식 */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-0 w-96 h-96 bg-[var(--accent)]/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-[var(--accent)]/5 rounded-full blur-3xl"></div>
      </div>

      <motion.div
        className="relative w-full max-w-md"
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <Card variant="glass">
          {/* 로고 */}
          <div className="flex justify-center mb-8">
            <Logo size="lg" />
          </div>

          {/* 제목 */}
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-[var(--text)] mb-2">계정 찾기</h2>
            <p className="text-[var(--text-secondary)]">아이디 또는 비밀번호를 찾을 수 있습니다</p>
          </div>

          {/* 탭 */}
          <div className="flex gap-2 mb-6">
            <button
              type="button"
              onClick={() => handleTabChange('username')}
              className={`flex-1 py-3 px-4 rounded-btn font-semibold transition-all ${
                activeTab === 'username'
                  ? 'bg-[var(--accent)] text-[var(--bg)]'
                  : 'bg-white/5 text-[var(--text-secondary)] hover:bg-white/10'
              }`}
            >
              <div className="flex items-center justify-center gap-2">
                <User className="w-5 h-5" />
                아이디 찾기
              </div>
            </button>
            <button
              type="button"
              onClick={() => handleTabChange('password')}
              className={`flex-1 py-3 px-4 rounded-btn font-semibold transition-all ${
                activeTab === 'password'
                  ? 'bg-[var(--accent)] text-[var(--bg)]'
                  : 'bg-white/5 text-[var(--text-secondary)] hover:bg-white/10'
              }`}
            >
              <div className="flex items-center justify-center gap-2">
                <Lock className="w-5 h-5" />
                비밀번호 찾기
              </div>
            </button>
          </div>

          {/* 성공 메시지 */}
          {isSubmitted && !hasError && (
            <motion.div
              className="mb-6 p-4 bg-[var(--accent)]/20 border border-[var(--accent)] rounded-btn"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
            >
              <div className="flex items-start gap-3">
                <Mail className="w-5 h-5 text-[var(--accent)] mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-[var(--text)] text-sm font-semibold mb-1">
                    이메일을 확인해주세요
                  </p>
                  <p className="text-[var(--text-secondary)] text-xs">
                    {activeTab === 'username'
                      ? '입력하신 정보로 등록된 이메일이 있다면 이메일을 발송했습니다.'
                      : '입력하신 이메일로 비밀번호 재설정 링크를 발송했습니다.'}
                  </p>
                  <p className="text-[var(--text-muted)] text-xs mt-2">
                    이메일이 도착하지 않았다면 스팸 폴더를 확인해주세요.
                  </p>
                </div>
              </div>
            </motion.div>
          )}

          {/* 에러 메시지 */}
          {hasError && !isSubmitted && (
            <motion.div
              className="mb-6 p-4 bg-[var(--danger)]/20 border border-[var(--danger)] rounded-btn flex items-center gap-2"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
            >
              <AlertCircle size={18} className="text-[var(--danger)] flex-shrink-0" />
              <p className="text-[var(--danger)] text-sm font-semibold">
                {emailError ||
                    usernameError ||
                    birthdateError ||
                    (apiError as any)?.response?.data?.error?.message ||
                    '요청 처리 중 오류가 발생했습니다.'}
              </p>
            </motion.div>
          )}

          {/* 폼 */}
          {!isSubmitted && (
            <form onSubmit={handleSubmit} className="space-y-6">
              {activeTab === 'username' ? (
                <>
                  <Input
                    type="text"
                    label="사용자명"
                    placeholder="사용자명을 입력하세요"
                    value={username}
                    onChange={handleUsernameChange}
                    onBlur={() => {
                      if (username) {
                        validateUsername(username);
                      }
                    }}
                    required
                    autoComplete="username"
                    leftIcon={
                      <User className="w-5 h-5" />
                    }
                    error={usernameError}
                  />

                  <div>
                    <label className="block text-sm font-medium text-[var(--text)] mb-2">
                      생년월일 <span className="text-[var(--accent)]">*</span>
                    </label>
                    <div className="flex gap-2">
                      <Input
                        type="text"
                        placeholder="YYYY"
                        value={birthYear}
                        onChange={(e) => {
                          const value = e.target.value.replace(/\D/g, '').slice(0, 4);
                          setBirthYear(value);
                          if (birthdateError && value.length === 4) {
                            setBirthdateError('');
                          }
                        }}
                        maxLength={4}
                        className="flex-1"
                        required
                      />
                      <Input
                        type="text"
                        placeholder="MM"
                        value={birthMonth}
                        onChange={(e) => {
                          const value = e.target.value.replace(/\D/g, '').slice(0, 2);
                          setBirthMonth(value);
                          if (birthdateError && value.length === 2) {
                            setBirthdateError('');
                          }
                        }}
                        maxLength={2}
                        className="flex-1"
                        required
                      />
                      <Input
                        type="text"
                        placeholder="DD"
                        value={birthDay}
                        onChange={(e) => {
                          const value = e.target.value.replace(/\D/g, '').slice(0, 2);
                          setBirthDay(value);
                          if (birthdateError && value.length === 2) {
                            setBirthdateError('');
                          }
                        }}
                        maxLength={2}
                        className="flex-1"
                        required
                      />
                    </div>
                    {birthdateError && (
                      <p className="mt-1 text-xs text-[var(--danger)]">{birthdateError}</p>
                    )}
                  </div>
                </>
              ) : (
                <Input
                  type="email"
                  label="이메일"
                  placeholder="your@email.com"
                  value={email}
                  onChange={handleEmailChange}
                  onBlur={() => {
                    if (email) {
                      validateEmail(email);
                    }
                  }}
                  required
                  autoComplete="email"
                  leftIcon={
                    <Mail className="w-5 h-5" />
                  }
                  error={emailError}
                />
              )}

              <Button
                type="submit"
                variant="primary"
                size="lg"
                fullWidth
                isLoading={isLoading}
                disabled={
                  activeTab === 'username'
                    ? !username.trim() || !birthYear || !birthMonth || !birthDay || !!usernameError || !!birthdateError
                    : !email.trim() || !!emailError
                }
              >
                {activeTab === 'username' ? '아이디 찾기' : '비밀번호 재설정 링크 보내기'}
              </Button>
            </form>
          )}

          {/* 다시 시도 버튼 (성공 후) */}
          {isSubmitted && !hasError && (
            <div className="mt-4">
              <Button
                type="button"
                variant="secondary"
                size="md"
                fullWidth
                onClick={handleReset}
              >
                {activeTab === 'username' ? '다시 찾기' : '다른 이메일로 시도하기'}
              </Button>
            </div>
          )}

          {/* 로그인 링크 */}
          <div className="mt-8 text-center">
            <Link
              to="/login"
              className="text-[var(--accent)] hover:opacity-80 font-semibold transition-colors text-sm"
            >
              ← 로그인으로 돌아가기
            </Link>
          </div>
        </Card>

        {/* 하단 텍스트 */}
        <motion.p
          className="text-center text-[var(--text-muted)] text-sm mt-8"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
        >
          헬스를 RPG처럼! 운동할수록 강해지세요 💪
        </motion.p>
      </motion.div>
    </div>
  );
}

